import { useState } from 'react'
import { Check, RotateCcw, Search } from 'lucide-react'
import {
  DEFAULT_PROGRESSION_IDS,
  DEFAULT_SCALE_IDS,
  PROGRESSION_LIBRARY,
  PROGRESSION_STYLES,
  SCALE_FAMILIES,
  SCALE_LIBRARY,
} from '../domain/curriculum'
import type { Preferences } from '../domain/types'

type LibrarySection = 'scales' | 'progressions'

type CurriculumLibraryProps = {
  preferences: Preferences
  onChange: (preferences: Preferences) => void
}

export function CurriculumLibrary({ preferences, onChange }: CurriculumLibraryProps) {
  const [section, setSection] = useState<LibrarySection>('scales')
  const [query, setQuery] = useState('')
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const selectedIds = section === 'scales' ? preferences.selectedScaleIds : preferences.selectedProgressionIds

  const updateIds = (ids: string[]) => {
    if (!ids.length) return
    if (section === 'scales') onChange({ ...preferences, selectedScaleIds: ids })
    else onChange({ ...preferences, selectedProgressionIds: ids })
  }

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      if (selectedIds.length === 1) return
      updateIds(selectedIds.filter((selectedId) => selectedId !== id))
    } else {
      updateIds([...selectedIds, id])
    }
  }

  const selectGroup = (ids: string[]) => updateIds([...new Set([...selectedIds, ...ids])])
  const reset = () => updateIds(section === 'scales' ? DEFAULT_SCALE_IDS : DEFAULT_PROGRESSION_IDS)

  return (
    <main id="main-content" className="page-shell curriculum-page">
      <section className="page-hero compact curriculum-hero">
        <div><h1>Curriculum Library</h1></div>
        <p>Choose the scales, modes, and style progressions used in your training rounds.</p>
      </section>

      <div className="library-toolbar">
        <div className="library-tabs" role="group" aria-label="Curriculum sections">
          <button className={section === 'scales' ? 'selected' : ''} type="button" onClick={() => { setSection('scales'); setQuery('') }} aria-pressed={section === 'scales'}>
            Scales &amp; Modes <span>{SCALE_LIBRARY.length}</span>
          </button>
          <button className={section === 'progressions' ? 'selected' : ''} type="button" onClick={() => { setSection('progressions'); setQuery('') }} aria-pressed={section === 'progressions'}>
            Style Progressions <span>{PROGRESSION_LIBRARY.length}</span>
          </button>
        </div>
        <label className="library-search">
          <Search aria-hidden="true" />
          <span className="sr-only">Search curriculum</span>
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={section === 'scales' ? 'Search scales or modes' : 'Search styles or progressions'} />
        </label>
      </div>

      <div className="library-summary">
        <span><strong>{selectedIds.length}</strong> selected for training</span>
        <button type="button" onClick={reset}><RotateCcw aria-hidden="true" /> Reset common set</button>
      </div>

      {section === 'scales' ? (
        <div className="curriculum-groups">
          {SCALE_FAMILIES.map((family) => {
            const items = SCALE_LIBRARY.filter((scale) => scale.family === family)
              .filter((scale) => !normalizedQuery || `${scale.name} ${scale.formula} ${scale.description}`.toLocaleLowerCase().includes(normalizedQuery))
            if (!items.length) return null
            return (
              <section className="curriculum-group" key={family}>
                <header><div><h2>{family}</h2><span>{items.length} entries</span></div><button type="button" onClick={() => selectGroup(items.map((item) => item.id))}>Select group</button></header>
                <div className="curriculum-item-grid">
                  {items.map((scale) => {
                    const checked = preferences.selectedScaleIds.includes(scale.id)
                    return (
                      <label className={checked ? 'curriculum-item checked' : 'curriculum-item'} key={scale.id}>
                        <input type="checkbox" checked={checked} disabled={checked && preferences.selectedScaleIds.length === 1} onChange={() => toggle(scale.id)} />
                        <span className="curriculum-check" aria-hidden="true">{checked && <Check />}</span>
                        <span className="curriculum-item-copy"><strong>{scale.name}</strong><b>{scale.formula}</b><small>{scale.description}</small></span>
                      </label>
                    )
                  })}
                </div>
              </section>
            )
          })}
        </div>
      ) : (
        <div className="curriculum-groups">
          {PROGRESSION_STYLES.map((style) => {
            const items = PROGRESSION_LIBRARY.filter((progression) => progression.style === style)
              .filter((progression) => !normalizedQuery || `${progression.name} ${progression.style} ${progression.minimum} ${progression.description}`.toLocaleLowerCase().includes(normalizedQuery))
            if (!items.length) return null
            return (
              <section className="curriculum-group" key={style}>
                <header><div><h2>{style}</h2><span>{items.length} progressions</span></div><button type="button" onClick={() => selectGroup(items.map((item) => item.id))}>Select group</button></header>
                <div className="curriculum-item-grid">
                  {items.map((progression) => {
                    const checked = preferences.selectedProgressionIds.includes(progression.id)
                    return (
                      <label className={checked ? 'curriculum-item checked' : 'curriculum-item'} key={progression.id}>
                        <input type="checkbox" checked={checked} disabled={checked && preferences.selectedProgressionIds.length === 1} onChange={() => toggle(progression.id)} />
                        <span className="curriculum-check" aria-hidden="true">{checked && <Check />}</span>
                        <span className="curriculum-item-copy"><strong>{progression.name}</strong><b>Available from {progression.minimum}</b><small>{progression.description}</small></span>
                      </label>
                    )
                  })}
                </div>
              </section>
            )
          })}
        </div>
      )}
    </main>
  )
}
